import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import { ClipLoader } from 'react-spinners';
import { FaPlus, FaEdit, FaTrash, FaCopy, FaEye, FaEyeSlash } from 'react-icons/fa';
import Helmet from 'react-helmet';
// --- API Service (api/codes.js) ---
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_API_URL_PRODUCTION
  : process.env.REACT_APP_API_URL;

const codesApi = {
  getAll: async () => {
    const res = await axios.get(`${API_BASE_URL}/api/codes`);
    return res.data;
  },
  create: async (data) => {
    const res = await axios.post(`${API_BASE_URL}/api/codes`, data);
    return res.data;
  },
  update: async (id, data) => {
    const res = await axios.put(`${API_BASE_URL}/api/codes/${id}`, data);
    return res.data;
  },
  remove: async (id) => {
    await axios.delete(`${API_BASE_URL}/api/codes/${id}`);
  },
};

// --- Custom Hook for Code Management ---
const useCodeManagement = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await codesApi.getAll();
      const sorted = data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setCodes(sorted);
    } catch (err) {
      console.error("Failed to fetch codes:", err);
      setError("Failed to load code snippets. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const addCode = async (newCode) => {
    try {
      const addedCode = await codesApi.create(newCode);
      setCodes(prev => [addedCode, ...prev].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      return true;
    } catch (err) {
      console.error("Failed to add code:", err);
      setError("Failed to add snippet. Please try again.");
      return false;
    }
  };

  const updateCode = async (id, updatedCode) => {
    try {
      const result = await codesApi.update(id, updatedCode);
      setCodes(prev => prev.map(code => (code._id === id ? result : code)));
      return true;
    } catch (err) {
      console.error("Failed to update code:", err);
      setError("Failed to update snippet. Please try again.");
      return false;
    }
  };

  const deleteCode = async (id) => {
    try {
      await codesApi.remove(id);
      setCodes(prev => prev.filter(code => code._id !== id));
      return true;
    } catch (err) {
      console.error("Failed to delete code:", err);
      setError("Failed to delete snippet. Please try again.");
      return false;
    }
  };

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  return { codes, loading, error, addCode, updateCode, deleteCode, fetchCodes };
};

// --- Custom Hook for Modal Logic ---
const useModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);
  return { isOpen, openModal, closeModal };
};


const Codes = () => {
  const { codes, loading, error, addCode, updateCode, deleteCode } = useCodeManagement();
  const { isOpen: isFormModalOpen, openModal: openFormModal, closeModal: closeFormModal } = useModal();
  const { isOpen: isDeleteConfirmModalOpen, openModal: openDeleteConfirmModal, closeModal: closeDeleteConfirmModal } = useModal();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [code, setCode] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [visibleCode, setVisibleCode] = useState({}); // Renamed from 'visible' for clarity
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState({}); // Renamed from 'showFullDesc' for clarity
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    Prism.highlightAll();
  }, [codes, visibleCode]); // Depend on visibleCode to re-highlight when code blocks are toggled

  // Initialize visibility and description expanded states
  useEffect(() => {
    setVisibleCode(codes.reduce((acc, item) => ({ ...acc, [item._id]: false }), {}));
    setIsDescriptionExpanded(codes.reduce((acc, item) => ({ ...acc, [item._id]: false }), {}));
  }, [codes]);


  const resetForm = () => {
    setTitle('');
    setDescription('');
    setCode('');
    setIsEditing(false);
    setEditId(null);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!title || !code) {
      alert('Title and Code are required.'); // Replace with a more sophisticated notification
      return;
    }

    const data = { title, description, code };
    let success;
    if (isEditing) {
      success = await updateCode(editId, data);
    } else {
      success = await addCode(data);
    }

    if (success) {
      resetForm();
      closeFormModal();
    }
  };

  const handleEdit = (item) => {
    setTitle(item.title);
    setDescription(item.description);
    setCode(item.code);
    setIsEditing(true);
    setEditId(item._id);
    openFormModal();
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    openDeleteConfirmModal();
  };

  const confirmDeleteAction = async () => {
    const success = await deleteCode(deleteId);
    if (success) {
      closeDeleteConfirmModal();
    }
  };

  const copyToClipboard = (item) => {
    navigator.clipboard.writeText(item.code).then(() => {
      setCopiedId(item._id);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(err => {
      console.error('Failed to copy text: ', err);
      // Optionally, show a user-friendly error message
    });
  };

  const highlight = (text, query) => {
    if (!query || typeof text !== 'string') return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? <span key={i} className="highlight">{part}</span> : part
    );
  };

  const toggleCodeVisibility = (id) => {
    setVisibleCode(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleDescriptionExpansion = (id) => {
    setIsDescriptionExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredCodes = codes.filter(c =>
    c.title.toLowerCase().includes(search.toLowerCase()) ||
    c.description.toLowerCase().includes(search.toLowerCase()) ||
    c.code.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filteredCodes.length / itemsPerPage));
  const paginatedCodes = filteredCodes.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="codes-wrapper">NavarroJalen | 
      <Helmet>
        <title>NavarroJalen | Codes</title>
        <meta name="description" content="NavarroJalen | Codes" />
        <meta property="og:title" content="NavarroJalen | Codes" />
      </Helmet>
      <div className="subMenuBar">
        <FaPlus className="subMenuBarButton" onClick={() => { resetForm(); openFormModal(); }} aria-label="Add new code snippet" />
        <input
          type="text"
          className="searchbar"
          placeholder="Search codes..."
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search code snippets"
        />
        {totalPages > 1 && (
          <div className="pagination">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="paginationButton" aria-label="Previous page">Prev</button>
            <span className="paginationInfo">Page {currentPage} of {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="paginationButton" aria-label="Next page">Next</button>
          </div>
        )}
      </div>

      {error && <p className="error-message">{error}</p>} {/* Display error message to user */}

      {isFormModalOpen && (
        <div className="modalOverlay" role="dialog" aria-modal="true" aria-labelledby="modalTitle">
          <div className="modalContent">
            <span className="codes-close-modal" onClick={closeFormModal} aria-label="Close modal">&times;</span>
            <h2 id="modalTitle" className="modalTitle">{isEditing ? 'Edit Code' : 'Add Code'}</h2>
            <form className="modalForm" onSubmit={handleFormSubmit}>
              <div className="modalInputDiv">
                <label htmlFor="title-input" className="modalInputLabel">Title <span className="label-require">*</span></label>
                <input
                  id="title-input"
                  className="modalInput"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  maxLength={100}
                />
              </div>
              <div className="modalInputDiv">
                <label htmlFor="description-textarea" className="modalInputLabel">Description</label>
                <textarea
                  id="description-textarea"
                  className="modalTextArea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div className="modalInputDiv">
                <label htmlFor="code-textarea" className="modalInputLabel">Code <span className="label-require">*</span></label>
                <textarea
                  id="code-textarea"
                  className="modalTextArea"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  rows={8}
                  required
                />
              </div>
              <div className="modalButtonDiv">
                <button type="submit" className="modalButton modalSubmitButton">{isEditing ? 'Update' : 'Add'}</button>
                <button type="button" className="modalButton modalCancelButton" onClick={closeFormModal}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading-spinner">
          <ClipLoader size={50} color="#66C0F4" />
        </div>
      ) : paginatedCodes.length === 0 ? (
        <p className="codes-emptySnippets-text">No code snippets found. {search ? "Try a different search, or add a new snippet." : "Click the '+' button to add your first snippet!"}</p>
      ) : (
        paginatedCodes.map((item) => (
          <div className="codes-code-snippet-container" key={item._id}>
            <div className="codes-header">
              <p className="codes-title">{highlight(item.title, search)}</p>
              <span className="codes-tags">code</span> {/* Consider making this dynamic based on language */}
            </div>
            <p className="codes-code-desc">
              {isDescriptionExpanded[item._id]
                ? highlight(item.description, search)
                : highlight(item.description.slice(0, 50) + (item.description.length > 50 ? '...' : ''), search)}
              {item.description.length > 50 && (
                <span className="codes-see-more" onClick={() => toggleDescriptionExpansion(item._id)} role="button" tabIndex="0" aria-expanded={isDescriptionExpanded[item._id]}>
                  {isDescriptionExpanded[item._id] ? 'See less' : 'See more'}
                </span>
              )}
            </p>
            <div className="codes-code-snippet-footer">
              <button onClick={() => toggleCodeVisibility(item._id)} aria-label={visibleCode[item._id] ? "Hide code" : "Show code"}>
                {visibleCode[item._id] ? <FaEyeSlash /> : <FaEye />}
              </button>
              <button onClick={() => copyToClipboard(item)} className={copiedId === item._id ? 'codes-copied' : ''} aria-label="Copy code to clipboard">
                <FaCopy />
              </button>
              <button onClick={() => handleEdit(item)} aria-label={`Edit ${item.title}`}>
                <FaEdit />
              </button>
              <button onClick={() => handleDeleteClick(item._id)} aria-label={`Delete ${item.title}`}>
                <FaTrash />
              </button>
            </div>
            {visibleCode[item._id] && (
              <pre className="codes-code-snippet-code-block">
                {/* Dynamically set language- class if you have a language field on your item */}
                <code className="language-js">{item.code}</code>
              </pre>
            )}
          </div>
        ))
      )}

      {isDeleteConfirmModalOpen && (
        <div className="modalOverlay" role="dialog" aria-modal="true" aria-labelledby="deleteModalTitle">
          <div className="modalAlertDiv">
            <h2 id="deleteModalTitle" className="modalTitle">Confirm Delete</h2>
            <p className="modalText">
              Are you sure you want to delete the snippet: <strong>{codes.find(c => c._id === deleteId)?.title || ''}</strong>?
            </p>
            <div className="modalButtonDiv">
              <button onClick={confirmDeleteAction} className="modalButton modalSubmitButton">Delete</button>
              <button onClick={closeDeleteConfirmModal} className="modalButton modalCancelButton">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Codes;